import { DeletionReason } from '@debbie/common/dist/debt-register/deletion-reason.js';
import { REGISTRY_CAPABILITIES } from '@debbie/common/dist/debt-register/denmark.js';
import { Registry, } from '@debbie/common/dist/debt-register/index.js';
import { parseOrStr } from '@debbie/common/dist/util/index.js';
import * as debtRegisterAPI from '@debbie/cortex/dist/api/debt-register.js';
import dbrLogo from '../../assets/images/debitor-registret-logo.png';
import rkiLogo from '../../assets/images/rki-partner-logo.png';
// Flatten a nested object into dot-path key/value rows so a raw provider
// response can be dumped as a table, e.g. { amount: { amount: 1234 } } becomes
// [['amount.amount', '1234']]. Arrays are joined, everything else is stringified.
const flatten = (value, prefix = '') => {
    if (value === null || value === undefined) {
        return [[prefix, '']];
    }
    if (Array.isArray(value)) {
        return [[prefix, value.join()]];
    }
    if (typeof value === 'object') {
        return Object.entries(value).flatMap(([k, v]) => flatten(v, prefix ? `${prefix}.${k}` : k));
    }
    return [[prefix, String(value)]];
};
export const registryDescriptors = {
    [Registry.RKI]: {
        registry: Registry.RKI,
        logo: rkiLogo,
        label: (lang) => lang.RKI.registration,
        isAvailable: (creditor) => creditor.experianClientId != null && creditor.experianClientId !== '',
        requiresFoundation: false,
        showRegistryType: true,
        actions: {
            status: {
                label: () => 'Tjek Status hos experian',
                call: (userId, caseId, registrationId) => debtRegisterAPI.rkiStatus(userId, caseId, registrationId),
            },
            delete: {
                label: () => 'Anmod Experian om sletning',
                supportedDeleteReasons: Object.values(DeletionReason),
                call: (userId, caseId, registrationId, reason) => debtRegisterAPI.rkiDelete(userId, caseId, registrationId, reason),
            },
            moveToOpen: {
                label: () => 'Tilføj Fundament',
                call: (userId, caseId, registrationId, { claim, files }) => debtRegisterAPI.rkiMoveToOpen(userId, caseId, registrationId, claim, files),
            },
            conclude: {
                label: () => 'Markér Uerholdelig',
                call: (userId, caseId, registrationId) => debtRegisterAPI.rkiConclude(userId, caseId, registrationId),
            },
            error: {
                label: () => 'Registrér status modtaget fra Experian',
                call: (userId, caseId, registrationId, error) => debtRegisterAPI.rkiError(userId, caseId, registrationId, error),
            },
        },
        api: {
            report: ({ userId, caseId, claim, personName, files }) => debtRegisterAPI.rkiReport(userId, caseId, claim, personName, files),
        },
        parseReportError: (error) => {
            const parsed = parseOrStr(error);
            if (parsed &&
                typeof parsed === 'object' &&
                'description' in parsed &&
                String(parsed.description).indexOf('[RKI]') !== -1) {
                return {
                    request: parsed.request,
                    description: parsed.description,
                };
            }
            return null;
        },
        statusHeader: () => 'Status hos Experian vedr. RKI-registrering',
        statusRows: (status) => {
            const rkiStatus = status;
            return Object.entries(rkiStatus.Results?.Status?.[0] ?? {}).map(([key, val]) => [
                key,
                Array.isArray(val) ? val.join() : String(val ?? ''),
            ]);
        },
        deleteHeader: () => 'Slet RKI-registrering',
        errorEntity: (lang) => lang.errorEntities.rkiRegistration,
    },
    [Registry.DEBITOR_REGISTRET]: {
        registry: Registry.DEBITOR_REGISTRET,
        logo: dbrLogo,
        label: (lang) => lang.debitorRegistret.registration,
        isAvailable: (creditor) => creditor.debitorRegistretCreditorNumber != null &&
            creditor.debitorRegistretCreditorNumber !== '',
        requiresFoundation: true,
        showRegistryType: false,
        actions: {
            status: {
                label: () => 'Tjek status hos Debitor Registret',
                call: (userId, caseId, registrationId) => debtRegisterAPI.dbrStatus(userId, caseId, registrationId),
            },
            delete: {
                label: () => 'Anmod om sletning',
                // DBR has no removal codes for GoodsReturned / ForcedSaleCancelled.
                supportedDeleteReasons: [
                    DeletionReason.Paid,
                    DeletionReason.AgreementMade,
                    DeletionReason.Deceased,
                    DeletionReason.CompositionFulfilled,
                    DeletionReason.DebtRelief,
                    DeletionReason.Disputed,
                    DeletionReason.Other,
                ],
                call: (userId, caseId, registrationId, reason) => debtRegisterAPI.dbrDelete(userId, caseId, registrationId, reason),
            },
        },
        api: {
            report: ({ userId, caseId, claim, personName }) => debtRegisterAPI.dbrReport(userId, caseId, claim, personName),
        },
        statusHeader: () => 'Status hos Debitor Registret',
        statusRows: (status) => flatten(status),
        deleteHeader: (lang) => `Slet ${lang.debitorRegistret.registration}`,
        errorEntity: (lang) => lang.debitorRegistret.registration,
    },
};
export const getDescriptor = (registry) => registryDescriptors[registry];
export const getAvailableRegistries = (creditor, opts) => Object.values(registryDescriptors).filter((d) => d.isAvailable(creditor) &&
    (!d.requiresFoundation || opts.withFoundation) &&
    (!opts.manglerAdvokatindestaaelse ||
        REGISTRY_CAPABILITIES[d.registry].supportsAdvokatindestaaelse));
//# sourceMappingURL=registries.js.map